import { Injectable, inject } from '@angular/core';
import { MemoizedSelector, Store } from '@ngrx/store';
import {
	Observable,
	Subject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	map,
	merge,
	mergeMap,
	of,
	startWith,
	take,
} from 'rxjs';
import type { IAuPaginationData } from '@app/shared/ui-components/au-paginator/pagination-page.info';
import { getSortKeyWithOrder } from '@app/pages/cmms/utils-and-classes/cmms-util-functions';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActionCenterService } from '@app/core/services/action-center.service';
import { MyBuildingLocationsService } from '@app/pages/my-building/my-building-locations.service';
import { getCurrentBuildingId } from '@app/pages/my-building/state/my-bulding.selectors';
import { CmmsFiltersService } from '../components/cmms-sort-filters-grouping/services/cmms-filters.service';
import { CmmsSortService } from '../components/cmms-sort-filters-grouping/services/cmms-sort.service';
import { AuCalendarLayoutService } from '@app/shared/ui-components/au-calendar-layout/au-calendar-layout.service';
import { isCalendarLayoutActive } from '../state/cmms.selectors';
import { ISelectedSortParams } from '../components/cmms-sort-filters-grouping/interfaces-and-types/sort-item.interfaces';
import { AuUtilsFunctions } from '@app/shared/au-utils';
import { defaultPageSize } from '../configs/pagination-query-variables';

type SearchTextField = 'name' | 'search';
type LocationIdsField = 'locations' | 'locationsAsset';
type PaginationSelector = MemoizedSelector<unknown, IAuPaginationData>;
type DefaultVariables = Record<string, unknown>;

export type CMMSQueryFilters<T extends IAuPaginationData = IAuPaginationData> = {
	isResultFiltered: boolean;
	variables: T & { isCalendarQuery?: boolean };
};

export type GetQueryInput = {
	searchTextField: SearchTextField;
	locationIdsField: LocationIdsField;
	defaultVariables?: DefaultVariables;
};

@Injectable()
export class CMMSLoadingFacade {
	private readonly store$ = inject(Store);
	private readonly filterService = inject(CmmsFiltersService);
	private readonly sortService = inject(CmmsSortService);
	private readonly actionCenterService = inject(ActionCenterService);
	private readonly calendarService = inject(AuCalendarLayoutService, { optional: true });
	private readonly locationService = inject(MyBuildingLocationsService);

	private readonly calendarDateRange$ =
		this.calendarService != null
			? toObservable(this.calendarService.calendarDateRange)
			: of(undefined);

	private readonly componentPagination$ = new Subject<IAuPaginationData>();
	private readonly reloadTrigger$ = new Subject<void>();

	public getQuery<T>(
		filters$: Observable<CMMSQueryFilters<T>>,
		pagination$: Observable<IAuPaginationData> = of(null)
	) {
		return combineLatest([filters$, pagination$]).pipe(
			debounceTime(50),
			map(([filters, pagination]) => ({
				isResultFiltered: filters.isResultFiltered,
				variables: removeUndefinedValues({ ...filters.variables, ...pagination }),
			}))
		);
	}

	public getPagination(paginationSelector: PaginationSelector) {
		return merge([
			this.store$.select(paginationSelector),
			this.componentPagination$,
		]).pipe(
			mergeMap(pagination => pagination),
			distinctUntilChanged((prev, curr) => isPaginationUnchanged(prev, curr))
		);
	}

	public updatePagination(pagination: IAuPaginationData) {
		this.componentPagination$.next(pagination);
	}

	public resetPagination(paginationSelector: PaginationSelector) {
		this.store$
			.select(paginationSelector)
			.pipe(take(1))
			.subscribe(currentPageination => {
				this.updatePagination({
					first: currentPageination?.first ?? currentPageination?.last ?? defaultPageSize,
				});
			});
	}

	public reload() {
		this.reloadTrigger$.next();
	}

	public getFilters(options: GetQueryInput) {
		return this.combineFilterSources().pipe(
			debounceTime(50),
			map(([selectedFilter, searchText, sortBy, locationIds, dates]) => {
				const isResultFiltered = getIsResultFiltered(selectedFilter, searchText);
				const variables = mapToFilterQueryVariables(
					options.defaultVariables ?? {},
					selectedFilter,
					searchText,
					options.searchTextField,
					sortBy,
					locationIds,
					options.locationIdsField,
					dates
				);
				return {
					isResultFiltered,
					variables,
				};
			})
		);
	}

	private combineFilterSources() {
		const calendarDates$ = this.store$
			.select(isCalendarLayoutActive)
			.pipe(
				mergeMap(isCalendarView =>
					isCalendarView ? this.calendarDateRange$ : of(undefined)
				)
			);

		const reloadTriggers$ = merge([
			this.actionCenterService.actionDone$,
			this.reloadTrigger$,
		]).pipe(
			mergeMap(trigger => trigger),
			startWith(undefined)
		);

		return combineLatest([
			this.filterService.selectedFilterVariablesFlattened$,
			this.filterService.quickSearchValue$,
			this.sortService.selectedSortParams$,
			this.locationService.getCurrentLocationIds(getCurrentBuildingId),
			calendarDates$,
			reloadTriggers$,
		]);
	}
}

function getIsResultFiltered(
	selectedFilter: Record<string, any>,
	searchText: string
): boolean {
	return Object.values(selectedFilter).length > 0 || searchText?.length > 0;
}

function mapToFilterQueryVariables(
	defaultVariables: DefaultVariables,
	filterVariables: { locations?: string[]; [key: string]: any },
	searchText: string | null,
	searchTextField: SearchTextField,
	sortParams: ISelectedSortParams | void,
	locationIds: string[],
	locationIdsField: LocationIdsField,
	dates?: { from: string; to: string } | undefined
) {
	const sortBy = getSortFilter(sortParams);

	const locations = mergeLocationFilter(filterVariables[locationIdsField], locationIds);

	const name = searchText?.length > 0 ? searchText : undefined;

	const calendarVariables =
		dates != null
			? {
					isCalendarQuery: true,
					calendarAfter: dates.from,
					calendarBefore: dates.to,
				}
			: {};

	const filterQueryVariables = {
		...defaultVariables,
		...filterVariables,
		...calendarVariables,
		[searchTextField]: name,
		[locationIdsField]: locations,
		sortBy,
	};
	return removeUndefinedValues(filterQueryVariables);
}

function removeUndefinedValues<T extends object>(obj: T): Partial<T> {
	return AuUtilsFunctions.filterObjUndefinedValues(obj) as Partial<typeof obj>;
}

function mergeLocationFilter(
	filterLocations: string[] | undefined,
	locationIds: string[] | undefined
): string[] | undefined {
	if (filterLocations != null && filterLocations.length > 0) {
		return filterLocations;
	}
	if (locationIds != null && locationIds.length > 0) {
		return locationIds;
	}
	return undefined;
}

function getSortFilter(sortParams: ISelectedSortParams | void): string | undefined {
	if (sortParams && sortParams.sortColumn) {
		return getSortKeyWithOrder(sortParams);
	}
	return undefined;
}

function isPaginationUnchanged(
	previous: IAuPaginationData | null,
	current: IAuPaginationData | null
): boolean {
	return (
		previous?.after == current?.after &&
		previous?.before == current?.before &&
		previous?.first == current?.first &&
		previous?.last == current?.last &&
		previous?.search == current?.search
	);
}
